import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {finalize, map, Observable, of, retry, shareReplay, Subject, throwError} from 'rxjs';
import {GenericResponse} from '../_types/shared';
import {
    Category,
    Community,
    NewQuestion,
    NewSubGroupPayload,
    Question,
    SubGroup,
    UserAccessLevel,
    UserCommunity
} from '../_types/communities';
import {fetchAllPages} from '../_utils/http-params';
import {User, UserWithModerationInfo} from '../_types/user';
import {AuthService} from './auth.service';
import {ContentService} from './content.service';
import {catchError} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class CommunitiesService {
    backgroundImage: string = '';
    backgroundImageChange: Subject<string> = new Subject<string>();
    private api = `${environment.apiUrl}/communities`;
    private cacheCommunities$: Observable<Community[]> | null = null;
    private cacheUserCommunities$: Observable<UserCommunity[]> | null = null;
    private cacheUserList$: Observable<UserWithModerationInfo[]> | null = null;

    constructor(
        private readonly http: HttpClient,
        private readonly authService: AuthService,
        private readonly contentService: ContentService,
    ) {
        this.backgroundImageChange.subscribe((value: string): void => {
            this.backgroundImage = value
        });
    }

    private _emailVerified!: boolean;

    get emailVerified(): boolean {
        return this._emailVerified;
    }

    set emailVerified(value: boolean) {
        this._emailVerified = value;
    }

    private _profileRefreshed!: boolean;

    get profileRefreshed(): boolean {
        return this._profileRefreshed;
    }

    set profileRefreshed(value: boolean) {
        this._profileRefreshed = value;
    }

    changeBackgroundImage(image: string): void {
        this.backgroundImageChange.next(image);
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.contentService.userHasCommunityRole(roles);
    }

    getAllCommunities(): Observable<Community[]> {
        if (!this.cacheCommunities$) {
            this.cacheCommunities$ = fetchAllPages<Community>(this.http, `${this.api}/active/`).pipe(
                finalize(() => this.cacheCommunities$ = null),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 500})
            );
        }
        return this.cacheCommunities$;
    }

    getCommunityByUrl(): Promise<any> {
        return new Promise((resolve, reject): void => {
            let parameters: HttpParams = new HttpParams();
            let url: string = window.location.origin;
            url = url.toLowerCase()
            // url = 'https://nebula.explainonline.co.uk/';
            parameters = parameters.set('retired', true);
            parameters = parameters.set('front_end_url', url);
            const MAX_RETRIES = 3;

            fetchAllPages<any>(this.http, `${this.api}/`, 1000, parameters)
                .pipe(
                    retry(MAX_RETRIES),
                    catchError((error): Observable<never> => {
                        console.error(`Request failed after ${MAX_RETRIES} attempts`, error);
                        return throwError(() => new Error('Request failed after multiple retries'));
                    })
                )
                .subscribe({
                    next: (data): void => {
                        let currentCommunity = null;
                        if (data.length === 0) {
                            return reject('No Community Found');
                        }
                        for (let community of data) {
                            if (
                                url.includes(community.front_end_url?.toLowerCase()) ||
                                community.front_end_url?.toLowerCase().includes(url) ||
                                community.front_end_url === url ||
                                url.includes(community.alternative_front_end_url?.toLowerCase()) ||
                                community.alternative_front_end_url?.toLowerCase().includes(url) ||
                                community.alternative_front_end_url === url
                            ) {
                                currentCommunity = community;
                                localStorage.setItem('lastSelectedCommunity', JSON.stringify(community));

                                community.logo
                                    ? localStorage.setItem('communityLogo', community.logo)
                                    : localStorage.removeItem('communityLogo');

                                community.name
                                    ? localStorage.setItem('communityName', community.name)
                                    : localStorage.removeItem('communityName');

                                community.background_image
                                    ? localStorage.setItem('communityLoginBG', community.background_image)
                                    : localStorage.removeItem('communityLoginBG');

                                this.changeBackgroundImage(community.main_background_image || '');
                                break;
                            }
                        }
                        currentCommunity ? resolve(currentCommunity) : reject('No Community Found');
                    },
                    error: (err): void => {
                        console.error('Final failure:', err);
                        reject('Request failed after multiple retries');
                    }
                });
        });
    }

    getUserCommunities(accessToken?: string): Observable<UserCommunity[]> {
        if (!this.cacheUserCommunities$ || accessToken) {
            if (accessToken) {
                localStorage.setItem('access_token', accessToken);
            }
            this.cacheUserCommunities$ = fetchAllPages<UserCommunity>(this.http, `${this.api}/my-memberships/`).pipe(
                finalize(() => this.cacheUserCommunities$ = null),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 500})
            );
        }
        return this.cacheUserCommunities$;
    }

    getAllEligibilityQuestions(): Observable<any> {
        return this.getAllCommunities()
            .pipe(
                map(res =>
                    res.flatMap(r => r.questions?.map(q => ({...q, communityName: r.company_name})) || []))
            );
    }

    getUserSubGroups(communityId: number, pageSize: number = 1000): Observable<SubGroup[]> {
        return fetchAllPages<SubGroup>(this.http, `${this.api}/${communityId}/my-subgroups/`, pageSize);
    }

    getAllSubGroups(communityId: number, pageSize: number = 1000): Observable<SubGroup[]> {
        return fetchAllPages<SubGroup>(this.http, `${this.api}/${communityId}/subgroups/`, pageSize);
    }

    // Getting subgroups depends on user permissions
    getSubGroups(communityId: number, pageSize: number = 1000): Observable<SubGroup[]> {
        return this.authService.isSuperUser() || this.hasCommunityRole(['client', 'full_admin', 'moderator', 'observer']) ? this.getAllSubGroups(communityId, pageSize) : this.getUserSubGroups(communityId, pageSize)
    }

    createSubgroup(communityId: number, payload: NewSubGroupPayload): Observable<SubGroup> {
        return this.http.post<SubGroup>(`${this.api}/${communityId}/subgroups/`, payload);
    }

    updateSubgroup(communityId: number, subgroupId: number, payload: {
        name?: string;
        description?: string;
        order?: number
    }): Observable<SubGroup> {
        return this.http.patch<SubGroup>(`${this.api}/${communityId}/subgroups/${subgroupId}/`, payload);
    }

    deleteSubgroup(communityId: number, subgroupId: number): Observable<void> {
        return this.http.delete<void>(`${this.api}/${communityId}/subgroups/${subgroupId}/`);
    }

    getCategories(communityId: number): Observable<Category[]> {
        return fetchAllPages<Category>(this.http, `${this.api}/${communityId}/categories/`);
    }

    createCategory(communityId: number, name: string): Observable<Category> {
        return this.http.post<Category>(`${this.api}/${communityId}/categories/`, {name});
    }

    updateCategory(communityId: number, categoryId: number, name: string): Observable<Category> {
        return this.http.patch<Category>(`${this.api}/${communityId}/categories/${categoryId}/`, {name});
    }

    deleteCategory(communityId: number, categoryId: number): Observable<void> {
        return this.http.delete<void>(`${this.api}/${communityId}/categories/${categoryId}/`);
    }

    getUserList(communityId: number): Observable<UserWithModerationInfo[]> {
        if (!this.cacheUserList$) {
            this.cacheUserList$ = fetchAllPages<UserWithModerationInfo>(this.http, `${this.api}/${communityId}/members/`).pipe(
                finalize(() => this.cacheUserList$ = null),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 60000})
            );
        }
        return this.cacheUserList$;
    }

    searchUserList(communityId: number, search: string): Observable<UserWithModerationInfo[]> {
        let parameters: HttpParams = new HttpParams();
        parameters = parameters.set('search', search);
        return fetchAllPages<any>(this.http, `${this.api}/${communityId}/members/`, 1000, parameters)
    }

    searchSimpleUserList(communityId: number, search: string): Observable<UserWithModerationInfo[]> {
        let parameters: HttpParams = new HttpParams();
        parameters = parameters.set('search', search);
        return fetchAllPages<any>(this.http, `${this.api}/${communityId}/members-stripped/`, 1000, parameters)
    }

    getUserInformationForCommunity(communityId: number): Observable<UserWithModerationInfo | null> {
        return this.http.get<GenericResponse<UserWithModerationInfo>>(`${this.api}/${communityId}/me/`)
            .pipe(
                map(res => res.data),
                catchError(err => {
                    return err.includes('User is not a member of community') ? of(null) : throwError(() => err);
                }));
    }

    getUserMembership(communityId: number, userId: number): Observable<any> {
        return this.http.get<any>(`${this.api}/${communityId}/member/${userId}/`);
    }

    addUserToSubgroup(communityId: number, subgroupId: number, userId: number[]): Observable<GenericResponse<string>> {
        return this.http.post<GenericResponse<string>>(`${this.api}/${communityId}/subgroups/${subgroupId}/add-members/`, {user_ids: userId});
    }

    removeUserFromSubgroup(communityId: number, subgroupId: number, userId: number): Observable<GenericResponse<string>> {
        return this.http.post<GenericResponse<string>>(`${this.api}/${communityId}/subgroups/${subgroupId}/remove-member/`, {user_id: userId});
    }

    subgroupMembers(communityId: number, subgroupId: number): Observable<User[]> {
        return fetchAllPages<{ user: User }>(this.http, `${this.api}/${communityId}/subgroups/${subgroupId}/members/`)
            .pipe(map(response => response.map(res => res.user)));
    }

    getQuestions(communityId: number): Observable<Question[]> {
        return fetchAllPages<Question>(this.http, `${this.api}/${communityId}/questions/`);
    }

    createNewQuestion(communityId: number, questionData: NewQuestion): Observable<Question> {
        return this.http.post<Question>(`${this.api}/${communityId}/questions/`, questionData);
    }

    updateQuestion(communityId: number, questionId: number, questionData: NewQuestion): Observable<Question> {
        return this.http.patch<Question>(`${this.api}/${communityId}/questions/${questionId}/`, questionData);
    }

    deleteQuestion(communityId: number, questionId: number): Observable<void> {
        return this.http.delete<void>(`${this.api}/${communityId}/questions/${questionId}/`);
    }

    editCommunityInviteEmail(communityId: number, template: string, subject?: string): Observable<any> {
        let body: any = {
            custom_invite_email_html: template
        }
        if (subject) {
            body['custom_invite_subject_text'] = subject;
        }
        return this.http.patch<any>(`${environment.apiUrl}/community/details/${communityId}/`, body);
    }

    editCommunityWelcomeEmail(communityId: number, template: string, subject?: string): Observable<any> {
        let body: any = {
            custom_welcome_email_html: template
        }
        if (subject) {
            body['custom_welcome_email_subject_text'] = subject;
        }
        return this.http.patch<any>(`${environment.apiUrl}/community/details/${communityId}/`, body);
    }
}

